import { useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { cercaOrdini, type MisuraManuale, type OrdineCercato } from "../api";

const CHIP =
  "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11.5px] font-semibold leading-tight";
/** Il blocco che si scioglie scrivendo colli e peso: lo stesso testo del backend. */
const BLOCCO_MISURE = "colli e peso mancanti: scrivili a mano";
/** Sotto questa lunghezza il backend non cerca (``MIN_CERCA``). */
const MIN_CERCA = 4;

type Props = {
  slot: string;
  /** Ordini già nella scheda o già aggiunti: niente secondo «Aggiungi». */
  presenti: Set<number>;
  disabilitato: boolean;
  onAggiungi: (ordine: OrdineCercato, misura: MisuraManuale | null) => void;
};

/** Ritiro libero: qualsiasi ordine, cercato per numero, rif. fornitore o LDV.
 *  Cio' che lo terrebbe fuori dalla scheda (corriere, annullo, magazzino) si
 *  legge negli avvisi e non blocca: decide chi prenota (Davide, 20/10). */
export function CercaOrdine({ slot, presenti, disabilitato, onAggiungi }: Props) {
  const [testo, setTesto] = useState("");
  const [trovati, setTrovati] = useState<OrdineCercato[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  async function cerca(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrore(null);
    try {
      setTrovati(await cercaOrdini(slot, testo.trim()));
    } catch (err) {
      setErrore(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-line px-5 py-4">
      <h3 className="mb-1 text-[12.5px] font-extrabold">Aggiungi un ordine qualsiasi</h3>
      <p className="mb-2 text-[12px]">
        Anche se non compare sopra: cercalo e mettilo nel ritiro di questo magazzino.
      </p>
      <form onSubmit={(e) => void cerca(e)} className="flex gap-2">
        <input
          type="search"
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
          placeholder="Numero ordine, rif. fornitore o LDV"
          aria-label="Cerca un ordine per il ritiro"
          maxLength={60}
          className="min-w-0 flex-1 rounded-lg border border-line-2 px-2 py-1.5 tabular-nums text-[12.5px] text-ink placeholder:text-ink"
        />
        <button
          type="submit"
          disabled={busy || testo.trim().length < MIN_CERCA}
          className="rounded-full border border-ink bg-ink px-3 py-1.5 text-[12.5px] font-bold text-on-ink hover:bg-ink/90 disabled:cursor-not-allowed disabled:border-line-2 disabled:bg-paper-2 disabled:text-ink"
        >
          {busy ? "Cerco…" : "Cerca"}
        </button>
      </form>
      {errore && (
        <p
          role="alert"
          className="mt-2 rounded-lg bg-red-soft px-3 py-2 text-[12.5px] font-semibold"
        >
          {errore}
        </p>
      )}
      {trovati?.length === 0 && (
        <p className="mt-2 text-[12.5px]">Nessun ordine trovato con «{testo.trim()}».</p>
      )}
      {trovati && trovati.length > 0 && (
        <ul className="mt-2 divide-y divide-line">
          {trovati.map((o) => (
            <Trovato
              key={o.order_id}
              ordine={o}
              presente={presenti.has(o.order_id)}
              disabilitato={disabilitato}
              onAggiungi={onAggiungi}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function Trovato({
  ordine: o,
  presente,
  disabilitato,
  onAggiungi,
}: {
  ordine: OrdineCercato;
  presente: boolean;
  disabilitato: boolean;
  onAggiungi: Props["onAggiungi"];
}) {
  const servonoMisure = o.blocco === BLOCCO_MISURE;
  const [colli, setColli] = useState("");
  const [kg, setKg] = useState("");
  const nColli = Number.parseInt(colli, 10);
  const nKg = Number.parseFloat(kg.replace(",", "."));
  const misureOk = nColli >= 1 && nColli <= 99 && nKg > 0 && nKg <= 3000;
  const fermo = o.blocco !== null && !servonoMisure;
  const puoAggiungere = !presente && !disabilitato && !fermo && (!servonoMisure || misureOk);

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
      <span className="min-w-0 flex-1">
        <span className="block tabular-nums text-[12px]">{o.rif_fornitore || o.reference}</span>
        <span className="block truncate text-[12px]">
          {o.cliente} · {o.canale}
          {o.ldv && (
            <>
              {" "}
              · LDV <span className="tabular-nums">{o.ldv}</span>
            </>
          )}
        </span>
      </span>
      {!servonoMisure && (
        <span className="text-[12px]">
          <b>
            {o.colli} {o.colli === 1 ? "collo" : "colli"}
          </b>{" "}
          · {o.peso_kg.toLocaleString("it-IT")} kg
        </span>
      )}
      {servonoMisure && (
        <span className="flex items-center gap-1.5 text-[12px]">
          <input
            inputMode="numeric"
            value={colli}
            onChange={(e) => setColli(e.target.value)}
            aria-label={`Colli di ${o.reference}`}
            placeholder="colli"
            className="w-14 rounded-lg border border-line-2 px-1.5 py-1 text-[12px] text-ink placeholder:text-ink"
          />
          <input
            inputMode="decimal"
            value={kg}
            onChange={(e) => setKg(e.target.value)}
            aria-label={`Peso in kg di ${o.reference}`}
            placeholder="kg"
            className="w-16 rounded-lg border border-line-2 px-1.5 py-1 text-[12px] text-ink placeholder:text-ink"
          />
        </span>
      )}
      <button
        type="button"
        disabled={!puoAggiungere}
        onClick={() =>
          onAggiungi(
            o,
            servonoMisure ? { order_id: o.order_id, colli: nColli, peso_kg: nKg } : null,
          )
        }
        className="rounded-full border border-ink px-2.5 py-1 text-[12px] font-bold text-ink disabled:cursor-not-allowed disabled:border-line-2 disabled:bg-paper-2"
      >
        {presente ? "Già in lista" : "Aggiungi"}
      </button>
      {(o.avvisi.length > 0 || fermo || servonoMisure) && (
        <span className="flex basis-full flex-wrap gap-1.5">
          {fermo && <span className={`${CHIP} bg-red-soft`}>{o.blocco}</span>}
          {servonoMisure && (
            <span className={`${CHIP} bg-amber-soft`}>misure non nel PIM: scrivi colli e kg</span>
          )}
          {o.avvisi.map((a) => (
            <span key={a} className={`${CHIP} bg-amber-soft`}>
              {a}
            </span>
          ))}
        </span>
      )}
    </li>
  );
}
